export const PORTAL_HALF_TURN = Math.PI;
export const PORTAL_HEIGHT = 13;
export const PORTAL_APERTURE_HALF_LENGTH = PORTAL_HEIGHT / 2;
export const PORTAL_SURFACE_HALF_THICKNESS = 0.75;

export const portalTransformAngle = (sourceAngle, targetAngle) =>
	targetAngle - sourceAngle + PORTAL_HALF_TURN;

export const rotatePortalVector = (vector, angle) => {
	const cos = Math.cos(angle);
	const sin = Math.sin(angle);
	return {
		x: vector.x * cos - vector.y * sin,
		y: vector.x * sin + vector.y * cos,
	};
};

export const transformPortalVector = (vector, sourceAngle, targetAngle) =>
	rotatePortalVector(vector, portalTransformAngle(sourceAngle, targetAngle));

export const transformPortalPoint = (point, sourcePosition, sourceAngle, targetPosition, targetAngle) => {
	const relative = {
		x: point.x - sourcePosition.x,
		y: point.y - sourcePosition.y,
	};
	const transformed = transformPortalVector(relative, sourceAngle, targetAngle);
	return {
		x: targetPosition.x + transformed.x,
		y: targetPosition.y + transformed.y,
	};
};

export const portalNormal = angle => ({
	x: Math.cos(angle),
	y: Math.sin(angle),
});

export const portalTangent = angle => ({
	x: -Math.sin(angle),
	y: Math.cos(angle),
});

export const signedPortalDistance = (point, portalPosition, portalAngle) => {
	const normal = portalNormal(portalAngle);
	return (point.x - portalPosition.x) * normal.x +
		(point.y - portalPosition.y) * normal.y;
};

export const portalTangentDistance = (point, portalPosition, portalAngle) => {
	const tangent = portalTangent(portalAngle);
	return (point.x - portalPosition.x) * tangent.x +
		(point.y - portalPosition.y) * tangent.y;
};

// Projects a tangent offset onto the finite portal mouth. Keeping this in the
// shared math module lets non-physics constraints (such as a rope guide) use
// exactly the same aperture convention as portal collision and rendering.
export const portalApertureGuidePoint = (
	portalPosition,
	portalAngle,
	tangentOffset,
	apertureHalfLength,
	inset = 0,
) => {
	const requestedOffset = Number.isFinite(tangentOffset) ? tangentOffset : 0;
	const halfLength = Number.isFinite(apertureHalfLength) ?
		Math.max(0, apertureHalfLength) : 0;
	const safeInset = Number.isFinite(inset) ? Math.max(0, inset) : 0;
	const limit = Math.max(0, halfLength - safeInset);
	const clampedOffset = Math.max(-limit, Math.min(limit, requestedOffset));
	const tangent = portalTangent(portalAngle);
	return {
		x: portalPosition.x + tangent.x * clampedOffset,
		y: portalPosition.y + tangent.y * clampedOffset,
		tangentOffset: clampedOffset,
		clamped: clampedOffset !== requestedOffset,
	};
};

// Chooses whether a rope should be free, remain wrapped around its current
// portal lip, or switch to the opposite lip after a very fast swing. Releasing
// uses a slightly smaller aperture than engaging so a rope resting exactly on
// the corner cannot chatter between both states.
export const resolvePortalApertureGuideTangent = (
	tangentOffset,
	apertureHalfLength,
	currentGuideTangent = null,
	inset = 0,
	releaseHysteresis = 0,
) => {
	const projection = portalApertureGuidePoint(
		{x: 0, y: 0},
		0,
		tangentOffset,
		apertureHalfLength,
		inset,
	);
	if(!Number.isFinite(currentGuideTangent)) {
		return projection.clamped ? projection.tangentOffset : null;
	}

	// If one physics step swept the direct rope all the way across the opening,
	// the old lip cannot still be the obstruction. Catch the opposite lip now.
	if(projection.tangentOffset * currentGuideTangent < 0) {
		return projection.clamped ? projection.tangentOffset : null;
	}

	const releaseProjection = portalApertureGuidePoint(
		{x: 0, y: 0},
		0,
		tangentOffset,
		apertureHalfLength,
		inset + Math.max(0, releaseHysteresis),
	);
	return releaseProjection.clamped ? currentGuideTangent : null;
};

export const normalizeAngle = angle => {
	while(angle > Math.PI) angle -= Math.PI * 2;
	while(angle <= -Math.PI) angle += Math.PI * 2;
	return angle;
};
